import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStreamAuth } from '../../hooks/useStreamAuth';
import { useProfile } from '../../contexts/ProfileContext';
import { useAppLoad } from '../../contexts/AppLoadContext';
import LoadingSpinner from './LoadingSpinner';
import spotlightService from '../../services/SpotlightService';
import { cacheService } from '../../services/CacheService';

const ProfileRedirectHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading: authLoading } = useStreamAuth();
  const { loading: appLoadLoading, needsProfileSelection: appNeedsProfileSelection } = useAppLoad();
  const {
    profiles,
    currentProfile,
    hasLoadedProfiles,
    isLoading: profileLoading,
    error: profileError,
    loadProfiles,
    needsProfileSelection,
    isSwitchingProfiles,
  } = useProfile();

  // Load profiles immediately when user is authenticated (don't wait for appload)
  // CRITICAL: Wait for SpotlightService initialization before loading profiles
  // This ensures client token is ready before profile API calls (especially important for Safari)
  useEffect(() => {
    const initializeAndLoadProfiles = async () => {
      // Prevent retry loops: don't retry if we've already attempted and there's a network error
      const isNetworkError = profileError && (
        profileError.includes('Failed to fetch') ||
        profileError.includes('Network error') ||
        profileError.includes('Unable to reach server')
      );
      
      if (isAuthenticated && user && !hasLoadedProfiles && !profileLoading && !isNetworkError) {
        console.log('🔧 ProfileRedirectHandler: Waiting for SpotlightService initialization...');
        
        // Wait for SpotlightService to finish initialization
        // This ensures the client token from Auth0 is properly set before we try to load profiles
        await spotlightService.initializationPromise;
        
        console.log('✅ ProfileRedirectHandler: SpotlightService initialized, loading profiles...');
        
        // Clear the session flag since we successfully initialized
        // This allows future token clear attempts if needed
        sessionStorage.removeItem('chillfree_token_clear_attempted');
        
        loadProfiles(); // Load profiles
      }
    };
    
    initializeAndLoadProfiles();
  }, [isAuthenticated, user, hasLoadedProfiles, profileLoading, loadProfiles]);

  // Handle profile loading errors with retry logic (with circuit breaker)
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const hasAttemptedTokenClear = useRef(false);
  
  // Use sessionStorage to track if we've already cleared tokens in this browser session
  // This prevents infinite loops where clearing tokens and redirecting still fails
  const SESSION_TOKEN_CLEAR_KEY = 'chillfree_token_clear_attempted';

  useEffect(() => {
    if (profileError && !hasLoadedProfiles && !profileLoading && retryCount < maxRetries) {
      // Check if this looks like a stale token error (network failure during auth)
      const isNetworkError = cacheService.shouldClearAuthTokens({ message: profileError });
      
      // Check if we've already tried clearing tokens in this session
      const alreadyClearedInSession = sessionStorage.getItem(SESSION_TOKEN_CLEAR_KEY) === 'true';
      
      if (isNetworkError && !hasAttemptedTokenClear.current && !alreadyClearedInSession) {
        // This appears to be a stale token issue - clear auth tokens and force re-auth
        console.warn('🔐 ProfileRedirectHandler: Network error detected, likely stale tokens. Clearing auth tokens...');
        hasAttemptedTokenClear.current = true;
        
        // Mark that we've attempted to clear tokens in this session
        sessionStorage.setItem(SESSION_TOKEN_CLEAR_KEY, 'true');
        
        // Clear only auth tokens (preserves user data like watch history)
        cacheService.clearAuthTokensOnly();
        
        // Force Auth0 to re-authenticate by triggering logout
        // The user will be prompted to log in again with fresh tokens
        console.warn('🔐 ProfileRedirectHandler: Redirecting to login for fresh authentication...');
        
        // Set a small delay to allow state to settle, then redirect
        setTimeout(() => {
          // Navigate to home - Auth0 will detect no valid session and prompt login
          window.location.href = '/';
        }, 500);
        
        return;
      } else if (alreadyClearedInSession && isNetworkError) {
        // We've already cleared tokens this session but still getting errors
        // This might be a deeper issue - log it but don't loop
        console.error('🔐 ProfileRedirectHandler: Token clear already attempted this session. Profile loading still failing.');
        console.error('🔐 ProfileRedirectHandler: This may indicate a network issue or backend problem.');
        // Don't redirect again, let the user see the error state
      }
      
      // Retry after a delay, but only if we haven't loaded profiles yet
      const retryTimer = setTimeout(() => {
        if (!hasLoadedProfiles && !profileLoading && retryCount < maxRetries) {
          console.warn(`ProfileRedirectHandler: Retrying profile load (attempt ${retryCount + 1}/${maxRetries})`);
          setRetryCount(prev => prev + 1);
          loadProfiles(); // Load profiles
        }
      }, 5000 * (retryCount + 1)); // Exponential backoff: 5s, 10s, 15s

      return () => clearTimeout(retryTimer);
    } else if (retryCount >= maxRetries) {
      // Max retries reached - clear tokens and force re-auth as last resort
      const alreadyClearedInSession = sessionStorage.getItem(SESSION_TOKEN_CLEAR_KEY) === 'true';
      
      if (!hasAttemptedTokenClear.current && !alreadyClearedInSession) {
        console.error('ProfileRedirectHandler: Max retries reached. Clearing auth tokens as last resort...');
        hasAttemptedTokenClear.current = true;
        sessionStorage.setItem(SESSION_TOKEN_CLEAR_KEY, 'true');
        cacheService.clearAuthTokensOnly();
        
        setTimeout(() => {
          window.location.href = '/';
        }, 500);
      } else {
        console.error('ProfileRedirectHandler: Max retries reached and token clear already attempted. Unable to load profiles.');
      }
    }
  }, [profileError, hasLoadedProfiles, profileLoading, loadProfiles, retryCount]);

  // Handle profile-based redirects
  useEffect(() => {
    // Don't redirect if still loading auth or profiles
    if (authLoading || profileLoading) {
      return;
    }

    // Don't redirect if not authenticated
    if (!isAuthenticated) {
      return;
    }

    // Don't redirect if we haven't loaded profiles yet
    if (!hasLoadedProfiles) {
      return;
    }

    // Don't redirect if already on profile-related pages
    if (location.pathname === '/profiles') {
      return;
    }

    // Don't redirect if user is explicitly switching profiles
    if (isSwitchingProfiles) {
      return;
    }

    let redirectTo = null;
    if (
      (location.pathname === '/' && location.search.length) ||
      location.pathname !== '/profiles'
    ) {
      redirectTo = '?after_selection=' + encodeURIComponent(location.pathname + location.search);
    }

    // Redirect to profile selection if needed
    if (needsProfileSelection()) {
      navigate('/profiles' + (redirectTo || ''), { replace: true });
      return;
    }

    // If user has profiles but no current profile selected, redirect to profiles
    // But only if they're not already on the profiles page (to avoid adding redirect params when switching)
    if (profiles.length > 0 && !currentProfile && location.pathname !== '/profiles') {
      navigate('/profiles' + (redirectTo || ''), { replace: true });
      return;
    }
  }, [
    authLoading,
    profileLoading,
    isAuthenticated,
    hasLoadedProfiles,
    location.pathname,
    needsProfileSelection,
    profiles.length,
    currentProfile,
    navigate,
    isSwitchingProfiles,
  ]);

  // Check if we're in a persistent error state
  const alreadyClearedInSession = sessionStorage.getItem(SESSION_TOKEN_CLEAR_KEY) === 'true';
  const isPersistentError = profileError && !hasLoadedProfiles && !profileLoading && retryCount >= maxRetries && alreadyClearedInSession;

  // Show error UI if we're in a persistent error state
  if (isPersistentError) {
    return (
      <div className="min-h-screen bg-page flex flex-col items-center justify-center p-4">
        <div className="bg-gray-900 rounded-lg p-8 max-w-md text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-white mb-4">Connection Issue</h1>
          <p className="text-gray-300 mb-6">
            We're having trouble connecting to the server. This could be due to a network issue or an expired session.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                // Clear the session flag and all auth tokens, then reload
                sessionStorage.removeItem(SESSION_TOKEN_CLEAR_KEY);
                cacheService.clearAuthTokensOnly();
                window.location.href = '/';
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => {
                // Full logout and clear everything
                sessionStorage.removeItem(SESSION_TOKEN_CLEAR_KEY);
                cacheService.clearAllCriticalData();
                window.location.href = '/';
              }}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Sign Out & Reset
            </button>
          </div>
          <p className="text-gray-500 text-sm mt-4">
            Error: {profileError}
          </p>
        </div>
      </div>
    );
  }

  // Show loading while checking authentication
  if (authLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // If AppLoadContext indicates we need profile selection, show loading
  // BUT allow the profiles page to render if we're already on it
  if (appNeedsProfileSelection && location.pathname !== '/profiles') {
    return <LoadingSpinner message="Loading profiles..." />;
  }

  // If authenticated but profiles are still loading, show loading
  if (isAuthenticated && (profileLoading || !hasLoadedProfiles)) {
    return <LoadingSpinner message="Loading profiles..." />;
  }

  // If authenticated and profiles are loaded, but need profile selection, show loading
  // BUT allow the profiles page to render if we're already on it
  if (
    isAuthenticated &&
    hasLoadedProfiles &&
    needsProfileSelection() &&
    location.pathname !== '/profiles'
  ) {
    return <LoadingSpinner message="Selecting profile..." />;
  }

  // If authenticated and profiles are loaded, but no current profile, show loading
  // BUT allow the profiles page to render if we're already on it
  if (
    isAuthenticated &&
    hasLoadedProfiles &&
    profiles.length > 0 &&
    !currentProfile &&
    location.pathname !== '/profiles'
  ) {
    return <LoadingSpinner message="Selecting profile..." />;
  }

  // Render children once everything is loaded and checked
  return children;
};

export default ProfileRedirectHandler;
